import { useState } from "react";
import { Modal } from "../components/ui";
import { Icon } from "../components/Icons";
import { FilmFrame } from "../components/FilmFrame";
import { actions } from "../lib/store";
import { filmsFor, isForProject, type FilmEntry } from "../lib/films";
import { relative } from "../lib/format";
import type { Project } from "../lib/types";

/* Nesting the studio's motion work in the project. The films are the studio's
   own — served from its own origin — so they play inside the portal instead of
   going out as a link that expires or gets forwarded to the wrong person. */

export function FilmsPanel({ p, by }: { p: Project; by: string }) {
  const films = p.films ?? [];
  const [adding, setAdding] = useState(false);
  const library = filmsFor(p).filter((f) => !films.some((x) => x.url === f.url));
  const suggested = library.filter((f) => isForProject(f, p));
  const others = library.filter((f) => !isForProject(f, p));

  return (
    <div className="col" style={{ gap: 18 }}>
      {/* films already nested */}
      {films.length === 0 ? (
        <div className="card pad" style={{ maxWidth: 640 }}>
          <div className="kicker">No film nested yet</div>
          <h3 className="serif" style={{ fontSize: 22, marginTop: 6 }}>Put the film in the portal</h3>
          <p className="muted" style={{ fontSize: 14, marginTop: 8, lineHeight: 1.65 }}>
            A motion film explains a massing idea in thirty seconds better than three paragraphs can. Nest one here and it
            plays inside {p.client?.name?.split(" ")[0] ?? "the client"}'s portal — no link to lose, no file to download.
          </p>
        </div>
      ) : (
        films.map((f) => (
          <div key={f.id} className="card" style={{ overflow: "hidden" }}>
            <FilmFrame film={f} height="clamp(300px, 48vh, 520px)" />
            <div style={{ padding: "13px 18px 15px" }}>
              <div className="row between wrap" style={{ gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div className="row" style={{ gap: 8, alignItems: "baseline" }}>
                    <h3 className="serif" style={{ fontSize: 19 }}>{f.title}</h3>
                    {f.sharedWithClient
                      ? <span className="badge ok" style={{ fontSize: 9 }}><Icon.check width={9} />In the portal</span>
                      : <span className="badge" style={{ fontSize: 9 }}>Studio only</span>}
                  </div>
                  {f.subtitle && <div className="dim" style={{ fontSize: 12.5, marginTop: 3 }}>{f.subtitle}</div>}
                  <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                    <span className="mono">{f.url}</span> · added {relative(f.addedOn)} by {f.addedBy}
                  </div>
                </div>
                <div className="row wrap" style={{ gap: 8 }}>
                  <a className="btn ghost sm" href={f.url} target="_blank" rel="noreferrer"><Icon.arrowUpRight width={13} />Open</a>
                  <button className={"btn sm " + (f.sharedWithClient ? "ghost" : "accent")}
                    onClick={() => actions.shareFilm(p.id, f.id, !f.sharedWithClient, by)}>
                    <Icon.send width={13} />{f.sharedWithClient ? "Take out of the portal" : "Show the client"}
                  </button>
                  <button className="iconbtn" style={{ width: 28, height: 28 }} title="Remove"
                    onClick={() => { if (confirm(`Remove “${f.title}” from this project?`)) actions.removeFilm(p.id, f.id); }}>
                    <Icon.close width={12} />
                  </button>
                </div>
              </div>
              {f.note && (
                <div className="panel" style={{ padding: "9px 12px", marginTop: 11 }}>
                  <div className="kicker" style={{ fontSize: 8 }}>What the client is told</div>
                  <div style={{ fontSize: 13, marginTop: 3, lineHeight: 1.55 }}>{f.note}</div>
                </div>
              )}
            </div>
          </div>
        ))
      )}

      {/* the studio's library, best match first */}
      {library.length > 0 && (
        <div className="card" style={{ overflow: "hidden" }}>
          <div className="row between wrap" style={{ padding: "13px 18px", borderBottom: "1px solid var(--line)", gap: 10 }}>
            <span className="row" style={{ gap: 8 }}><Icon.present width={15} style={{ color: "var(--accent-ink)" }} /><span className="kicker">Studio film library</span></span>
            <button className="btn ghost sm" onClick={() => setAdding(true)}><Icon.link width={13} />Add by URL</button>
          </div>
          {suggested.length > 0 && (
            <div style={{ padding: "12px 18px 4px" }}>
              <div className="dim" style={{ fontSize: 11.5, marginBottom: 8 }}>Made for this project</div>
              {suggested.map((f) => <LibraryRow key={f.key} f={f} p={p} by={by} highlight />)}
            </div>
          )}
          {others.length > 0 && (
            <div style={{ padding: "8px 18px 14px", borderTop: suggested.length ? "1px solid var(--line)" : "none", marginTop: suggested.length ? 10 : 0 }}>
              <div className="dim" style={{ fontSize: 11.5, margin: "10px 0 8px" }}>Other studio films</div>
              {others.map((f) => <LibraryRow key={f.key} f={f} p={p} by={by} />)}
            </div>
          )}
        </div>
      )}

      {adding && <AddFilmModal p={p} by={by} onClose={() => setAdding(false)} />}
    </div>
  );
}

function LibraryRow({ f, p, by, highlight }: { f: FilmEntry; p: Project; by: string; highlight?: boolean }) {
  return (
    <div className="row between wrap" style={{ gap: 12, padding: "10px 12px", borderRadius: 8, background: highlight ? "var(--paper-2)" : "transparent", marginBottom: 6 }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="row wrap" style={{ gap: 7, alignItems: "baseline" }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>{f.title}</span>
          {f.forClient && <span className="badge" style={{ fontSize: 8.5 }}>{f.forClient}</span>}
        </div>
        <div className="dim" style={{ fontSize: 12, marginTop: 2, lineHeight: 1.5 }}>{f.subtitle}</div>
      </div>
      <div className="row wrap" style={{ gap: 6, flexShrink: 0 }}>
        <a className="chip" style={{ fontSize: 11 }} href={f.url} target="_blank" rel="noreferrer">Preview</a>
        <button className="btn ghost sm" onClick={() => actions.addFilm(p.id, { ...f, by, share: false })}><Icon.plus width={13} />Nest it</button>
        <button className="btn accent sm" onClick={() => actions.addFilm(p.id, { ...f, by, share: true })}><Icon.send width={13} />Nest &amp; show client</button>
      </div>
    </div>
  );
}

function AddFilmModal({ p, by, onClose }: { p: Project; by: string; onClose: () => void }) {
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [url, setUrl] = useState("");
  const [note, setNote] = useState("");
  const [share, setShare] = useState(true);
  const ok = title.trim() && /^(https?:\/\/|\/)/.test(url.trim());

  return (
    <Modal open onClose={onClose} title="Nest a film by URL" wide>
      <div className="col" style={{ gap: 13 }}>
        <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.6 }}>
          Any page the studio hosts — a motion film, an interactive board, a walkthrough. It's framed inside the portal, so
          use a URL you control; a page that blocks framing will show blank.
        </p>
        <div className="field"><label>Title</label><input className="input" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Intention" /></div>
        <div className="field"><label>One line under the title</label><input className="input" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="e.g. The idea, before the building" /></div>
        <div className="field"><label>URL</label><input className="input mono" style={{ fontSize: 12.5 }} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="/films/… or https://…" /></div>
        <div className="field"><label>What the client should take from it</label>
          <textarea className="input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Watch this before the options — it explains where the massing comes from." />
        </div>
        <label className="row" style={{ gap: 8, fontSize: 13.5, cursor: "pointer" }}>
          <input type="checkbox" checked={share} onChange={(e) => setShare(e.target.checked)} />
          Show it in the client portal now (they'll be emailed)
        </label>
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn accent" disabled={!ok} onClick={() => { actions.addFilm(p.id, { title, subtitle, url, note, by, share }); onClose(); }}>
            <Icon.check width={15} />Nest it
          </button>
        </div>
      </div>
    </Modal>
  );
}
